/**
 * TIDRAPPORTENS LÄSNING — SYNC_PULL in i en ögonblicksbild skärmarna kan rita.
 *
 * VARFÖR DEN FINNS. Det finns ingen skriv- eller läsrutt per tabell i
 * kontraktet: ALL företagsdata kommer ur SYNC_PULL och går in genom SYNC_PUSH
 * (shared/types.ts avsnitt 8). Steg 5 äger IndexedDB, utkorgen och
 * bakgrundssynken; tills dess hålls raderna HÄR, i minnet, så att de fyra
 * skärmarna i tidrapporten kan visa RIKTIG data i stället för platshållare. Det
 * är en LÄSCACHE och ingen utkorg — en push som misslyckas ligger inte kvar över
 * en omladdning. Se LUCKOR.
 *
 * TRE REGLER UR KONTRAKTET SOM MÅSTE HÅLLA HÄR, ORDAGRANT:
 *
 *   1. MARKÖREN. next_since är högsta LÄSTA seq, även för rader vyn filtrerade
 *      bort. has_more = true betyder BARA att fler seq återstår — INTE att
 *      changes är icke-tom. En pull som ger noll rader med has_more = true är
 *      NORMAL i montörs- och gästläge och måste följas av en ny pull på
 *      next_since. Loopen nedan gör just det.
 *
 *   2. VYBYTET. Skiljer sig `view` från förra svarets MÅSTE hela den lokala
 *      databasen tömmas och allt börja om på since = 0 — servern skickar aldrig
 *      om rader med lägre seq, så en BEFORDRAD montör skulle annars aldrig få
 *      prisfälten och en DEGRADERAD chef skulle behålla dem. (Utkorgen töms
 *      ALDRIG vid ett vybyte; någon utkorg finns inte här än.)
 *
 *   3. EFTER EN LYCKAD PUSH KOMMER ALLTID EN PULL. Push-svaret bär aldrig
 *      radinnehåll.
 *
 * BESLUT 16 HÅLLS AV TYPERNA, INTE AV DISCIPLIN. Ögonblicksbilden bär
 * *WorkerRow-typerna — TimeEntryWorkerRow, MileageWorkerRow, CustomerWorkerRow,
 * WorkOrderWorkerRow. En full rad är strukturellt en montörsrad med fler fält,
 * så chefens svar passar in utan omvandling, men INGEN skärm kan läsa ett
 * timpris ur bilden: fältet finns inte i typen. Det enda pris som släpps fram är
 * firmans milersättning, och den kommer ur company_settings, som bara finns i
 * FullChangeSet — därav `milersattning: null` i montörs- och gästläge.
 */

import { useEffect, useSyncExternalStore } from 'react';
import { SYNC_PULL, SYNC_PUSH } from '@delat/routes';
import { asSeq } from '@delat/types';
import type {
  AbsenceRow,
  ClientView,
  CompanyId,
  CustomerWorkerRow,
  ExpenseWorkerRow,
  MemberWorkerRow,
  MileageWorkerRow,
  Millis,
  OrePer10Km,
  Seq,
  SyncOp,
  SyncPullResponse,
  SyncPushResponse,
  TaskRow,
  TimeEntryWorkerRow,
  WeekStatusRow,
  WorkOrderWorkerRow,
} from '@delat/types';
import { enhetsId, klienten } from '../../api';
import type { Klartidfel, Resultat } from '../../api';
import { granskaSyncPull, granskaSyncPush } from '../../api/granskare/tidrapport';
import { registreraFirmaminne } from '../../app/firmaminne';

/**
 * Tabellerna tidrapporten faktiskt ritar. price_list, price_rate,
 * work_order_guest, work_order_photo, invoice_run och material_line hämtas med
 * flit INTE hit: de hör till andra skärmar, och en cache som bar allt vore en
 * andra sanning bredvid steg 5:s riktiga lager.
 */
export interface Ogonblicksbild {
  readonly vy: ClientView;
  readonly tidrader: readonly TimeEntryWorkerRow[];
  readonly milrader: readonly MileageWorkerRow[];
  readonly utlaggsrader: readonly ExpenseWorkerRow[];
  readonly franvaro: readonly AbsenceRow[];
  readonly veckor: readonly WeekStatusRow[];
  readonly ordrar: readonly WorkOrderWorkerRow[];
  readonly kunder: readonly CustomerWorkerRow[];
  readonly moment: readonly TaskRow[];
  readonly medlemmar: readonly MemberWorkerRow[];
  /**
   * Firmans milpris, ex moms. NULL i montörs- och gästläge — company_settings
   * finns inte i deras ändringsmängd (beslut 16), så milbeloppet i kronor går
   * helt enkelt inte att räkna fram där, och ska inte heller visas.
   */
  readonly milersattning: OrePer10Km | null;
  readonly hamtad_vid: Millis;
}

export interface Lagerstat {
  readonly firma: CompanyId | null;
  readonly laddar: boolean;
  readonly fel: Klartidfel | null;
  readonly bild: Ogonblicksbild | null;
}

/** Tak för antal pull-varv i en synk. 500 varv * 500 rader = 250 000 rader. */
const MAX_VARV = 500;

/** Så färsk en bild får vara utan att en ny synk körs vid en skärmväxling. */
const FARSKHET_MS = 15_000;

interface Kartor {
  readonly tid: Map<string, TimeEntryWorkerRow>;
  readonly mil: Map<string, MileageWorkerRow>;
  readonly utlagg: Map<string, ExpenseWorkerRow>;
  readonly franvaro: Map<string, AbsenceRow>;
  readonly veckor: Map<string, WeekStatusRow>;
  readonly ordrar: Map<string, WorkOrderWorkerRow>;
  readonly kunder: Map<string, CustomerWorkerRow>;
  readonly moment: Map<string, TaskRow>;
  readonly medlemmar: Map<string, MemberWorkerRow>;
}

const nyaKartor = (): Kartor => ({
  tid: new Map(),
  mil: new Map(),
  utlagg: new Map(),
  franvaro: new Map(),
  veckor: new Map(),
  ordrar: new Map(),
  kunder: new Map(),
  moment: new Map(),
  medlemmar: new Map(),
});

const slaIn = <T>(
  karta: Map<string, T>,
  rader: readonly T[],
  nyckel: (rad: T) => string,
): void => {
  for (const rad of rader) karta.set(nyckel(rad), rad);
};

/**
 * MJUKRADERADE RADER LIGGER KVAR I KARTAN och filtreras bort först här. En
 * radering är en NY VERSION av raden, inte ett borttagande: kastades den ur
 * kartan skulle en senare återupplivning (base_version = den mjukraderade
 * radens version) inte ha något att ersätta.
 */
const levande = <T extends { readonly deleted_at: Millis | null }>(
  karta: Map<string, T>,
): readonly T[] => [...karta.values()].filter((rad) => rad.deleted_at === null);

/** week_status har ingen id-kolumn — nyckeln är personen och veckan. */
const veckonyckel = (rad: WeekStatusRow): string => `${rad.account_id}|${rad.iso_week}`;

class Synklager {
  #firma: CompanyId | null = null;
  #vy: ClientView | null = null;
  #since: Seq = asSeq(0);
  #rader: Kartor = nyaKartor();
  #milersattning: OrePer10Km | null = null;
  #senast = 0;
  #ko: Promise<void> = Promise.resolve();

  #stat: Lagerstat = { firma: null, laddar: false, fel: null, bild: null };
  readonly #lyssnare = new Set<() => void>();

  /** Ögonblicksbilden. SAMMA objekt så länge ingenting ändrats. */
  las = (): Lagerstat => this.#stat;

  prenumerera = (lyssnare: () => void): (() => void) => {
    this.#lyssnare.add(lyssnare);
    return () => {
      this.#lyssnare.delete(lyssnare);
    };
  };

  #satt(nytt: Lagerstat): void {
    this.#stat = nytt;
    for (const lyssnare of this.#lyssnare) lyssnare();
  }

  #nollstall(firma: CompanyId): void {
    this.#firma = firma;
    this.#vy = null;
    this.#since = asSeq(0);
    this.#rader = nyaKartor();
    this.#milersattning = null;
    this.#senast = 0;
  }

  /**
   * ALLT UT UR MINNET. Vid utloggning och firmabyte (app/firmaminne.ts): lagret
   * lever i modulen och appen laddas aldrig om, så utan det här hade nästa
   * inloggning i samma flik fått föregående användares tidrader ur minnet.
   * Till skillnad från #nollstall töms också den publicerade ögonblicksbilden —
   * den är det skärmarna faktiskt ritar.
   */
  glom = (): void => {
    this.#firma = null;
    this.#vy = null;
    this.#since = asSeq(0);
    this.#rader = nyaKartor();
    this.#milersattning = null;
    this.#senast = 0;
    this.#satt({ firma: null, laddar: false, fel: null, bild: null });
  };

  /**
   * En synk i taget, i kö. Två skärmar som monteras samtidigt ska inte läsa
   * samma fönster parallellt och skriva över varandras markörer.
   */
  synka = (firma: CompanyId, tvinga = false): Promise<void> => {
    const nasta = this.#ko.then(
      () => this.#kor(firma, tvinga),
      () => this.#kor(firma, tvinga),
    );
    this.#ko = nasta;
    return nasta;
  };

  /**
   * SYNC_PUSH och därefter ALLTID en SYNC_PULL (shared/types.ts avsnitt 8:
   * "Push-svaret bär ALDRIG radinnehåll; klienten kör alltid en SYNC_PULL
   * direkt efter en lyckad push").
   */
  skicka = async (
    firma: CompanyId,
    ops: readonly SyncOp[],
  ): Promise<Resultat<SyncPushResponse>> => {
    const svar = await klienten.anrop(
      SYNC_PUSH,
      { params: { company_id: firma }, data: { device_id: enhetsId(), ops } },
      granskaSyncPush,
    );
    if (svar.ok) await this.synka(firma, true);
    return svar;
  };

  async #kor(firma: CompanyId, tvinga: boolean): Promise<void> {
    if (this.#firma !== firma) this.#nollstall(firma);
    if (!tvinga && this.#stat.bild !== null && Date.now() - this.#senast < FARSKHET_MS) {
      return;
    }
    this.#satt({ firma, laddar: true, fel: null, bild: this.#stat.bild });

    for (let varv = 0; varv < MAX_VARV; varv += 1) {
      const svar = await klienten.anrop(
        SYNC_PULL,
        { params: { company_id: firma }, data: { since: this.#since } },
        granskaSyncPull,
      );
      if (!svar.ok) {
        this.#satt({ firma, laddar: false, fel: svar.fel, bild: this.#stat.bild });
        return;
      }
      const sida = svar.varde;

      // VYBYTE: töm hela den lokala databasen och börja om på since = 0.
      if (this.#vy !== null && this.#vy !== sida.view) {
        this.#nollstall(firma);
        continue;
      }
      this.#vy = sida.view;
      this.#slaIhop(sida);
      this.#since = sida.next_since;

      if (!sida.has_more) {
        this.#senast = Date.now();
        this.#satt({
          firma,
          laddar: false,
          fel: null,
          bild: this.#byggBild(sida.view, sida.server_time),
        });
        return;
      }
    }
    // Kom vi hit gav servern has_more i MAX_VARV varv i rad. Bilden är då
    // ofullständig och får inte visas som färdig.
    this.#satt({
      firma,
      laddar: false,
      fel: {
        slag: 'obegripligt',
        http_status: 200,
        orsak: `synken gav has_more i ${MAX_VARV} varv i rad`,
        text: 'Det gick inte att hämta hela firmans data. Försök igen om en stund.',
      },
      bild: this.#stat.bild,
    });
  }

  /**
   * Grenen på `view` är själva sekretessgränsen. Varje gren ser bara sin egen
   * ändringsmängd, så 'worker' kan inte röra company_settings — fältet finns
   * inte i WorkerChangeSet.
   */
  #slaIhop(sida: SyncPullResponse): void {
    const r = this.#rader;
    if (sida.view === 'full') {
      const c = sida.changes;
      const installningar = c.company_settings[c.company_settings.length - 1];
      if (installningar !== undefined) {
        this.#milersattning = installningar.mileage_ore_per_10km;
      }
      slaIn(r.medlemmar, c.member, (x) => x.account_id);
      slaIn(r.kunder, c.customer, (x) => x.id);
      slaIn(r.ordrar, c.work_order, (x) => x.id);
      slaIn(r.moment, c.task, (x) => x.id);
      slaIn(r.tid, c.time_entry, (x) => x.id);
      slaIn(r.mil, c.mileage, (x) => x.id);
      slaIn(r.utlagg, c.expense, (x) => x.id);
      slaIn(r.franvaro, c.absence, (x) => x.id);
      slaIn(r.veckor, c.week_status, veckonyckel);
      return;
    }
    const c = sida.changes;
    slaIn(r.medlemmar, c.member, (x) => x.account_id);
    slaIn(r.kunder, c.customer, (x) => x.id);
    slaIn(r.ordrar, c.work_order, (x) => x.id);
    slaIn(r.moment, c.task, (x) => x.id);
    slaIn(r.tid, c.time_entry, (x) => x.id);
    slaIn(r.mil, c.mileage, (x) => x.id);
    slaIn(r.utlagg, c.expense, (x) => x.id);
    slaIn(r.franvaro, c.absence, (x) => x.id);
    slaIn(r.veckor, c.week_status, veckonyckel);
  }

  #byggBild(vy: ClientView, nu: Millis): Ogonblicksbild {
    const r = this.#rader;
    return {
      vy,
      tidrader: levande(r.tid),
      milrader: levande(r.mil),
      utlaggsrader: levande(r.utlagg),
      franvaro: levande(r.franvaro),
      veckor: levande(r.veckor),
      ordrar: levande(r.ordrar),
      kunder: levande(r.kunder),
      moment: levande(r.moment),
      medlemmar: levande(r.medlemmar),
      milersattning: vy === 'full' ? this.#milersattning : null,
      hamtad_vid: nu,
    };
  }
}

/** Appens enda läscache för tidrapporten. */
export const synklagret = new Synklager();

/** Töms vid utloggning och firmabyte tillsammans med de andra lagren. */
registreraFirmaminne(synklagret.glom);

export interface Synk extends Lagerstat {
  /** Hämtar allt sedan markören. `tvinga` går förbi färskhetsfönstret. */
  readonly synka: (tvinga?: boolean) => Promise<void>;
  readonly skicka: (ops: readonly SyncOp[]) => Promise<Resultat<SyncPushResponse>>;
}

/**
 * Skärmens ingång. Synkar vid montering och ritar om när bilden byts.
 * Bilden är `null` tills första synken gått igenom — visa <Laddar> då.
 */
export const useSynk = (firma: CompanyId): Synk => {
  const stat = useSyncExternalStore(synklagret.prenumerera, synklagret.las);

  useEffect(() => {
    void synklagret.synka(firma);
  }, [firma]);

  return {
    ...stat,
    synka: (tvinga = false) => synklagret.synka(firma, tvinga),
    skicka: (ops) => synklagret.skicka(firma, ops),
  };
};
